import {
  type Address,
  type Hex,
  concat,
  encodeAbiParameters,
  encodeFunctionData,
  toFunctionSelector,
} from 'viem'
import { mainnet } from 'viem/chains'
import { ASSET_LOGO_SRCS } from '../assets'
import { FUNKIT_NATIVE_TOKEN } from '../constants'
import type { ApiFunkitCheckoutActionParams } from '../types'
import { UNWRAP_AND_CALL_ABI, VAULT_DEPOSITOR_ABI } from './abi'
import { LIGHTER_DEPOSIT_ADDRESS } from './consts'
import {
  AMOUNT_PLACEHOLDER,
  UNWRAP_AND_CALL_ADDRESS,
  VAULT_DEPOSITOR_ADDRESS,
  tokenAddresses,
} from './consts'
import type { DynamicRoute, FunAddress } from './types'
import {
  MAX_APPROVAL,
  createPath,
  createRoute,
  getApprovalCalldata,
  getResetApprovalCalldata,
  shouldResetApproval,
} from './utils'

type FunkitCheckoutActionParams = ApiFunkitCheckoutActionParams
const NATIVE_TOKEN = FUNKIT_NATIVE_TOKEN

const RELAY_LIGHTER_CHAIN_ID = 3586256
const RELAY_LIGHTER_ETH_SPOT_ADDRESS =
  '0x0000000000000000000000000000000000000001' satisfies Address
const RELAY_LIGHTER_USDC_PERPS_ADDRESS =
  '0x0000000000000000000000000000000000000000' satisfies Address
const RELAY_LIGHTER_USDC_SPOT_ADDRESS =
  '0x0000000000000000000000000000000000000003' satisfies Address
enum RouteType {
  Perps = 0,
  Spot = 1,
}

enum AssetIndex {
  USDC = 3,
  ETH = 1,
  LIT = 2,
  LINK = 5,
  UNI = 6,
  AAVE = 7,
  SKY = 8,
  LDO = 9,
  AZTEC = 10,
}
// get from https://etherscan.io/address/0x3b4d794a66304f130a4db8f2551b0070dfcf5ca7#readProxyContract#F26
const TICK_SIZE_FOR_ASSET_INDEX = {
  [AssetIndex.USDC]: BigInt(1),
  [AssetIndex.ETH]: BigInt(10 ** 10),
  [AssetIndex.LIT]: BigInt(10 ** 10),
  [AssetIndex.LINK]: BigInt(10 ** 10),
  [AssetIndex.UNI]: BigInt(10 ** 10),
  [AssetIndex.AAVE]: BigInt(10 ** 10),
  [AssetIndex.SKY]: BigInt(10 ** 10),
  [AssetIndex.LDO]: BigInt(10 ** 10),
  [AssetIndex.AZTEC]: BigInt(10 ** 10),
}
// get from https://etherscan.io/address/0x3b4d794a66304f130a4db8f2551b0070dfcf5ca7#readProxyContract#F26
const DEPOSIT_TOKEN_FOR_ASSET_INDEX = {
  [AssetIndex.USDC]: tokenAddresses.USDC[mainnet.id],
  [AssetIndex.ETH]: NATIVE_TOKEN as Address,
  [AssetIndex.LIT]: tokenAddresses.LIT[mainnet.id],
  [AssetIndex.LINK]: tokenAddresses.LINK[mainnet.id],
  [AssetIndex.UNI]: tokenAddresses.UNI[mainnet.id],
  [AssetIndex.AAVE]: tokenAddresses.AAVE[mainnet.id],
  [AssetIndex.SKY]: tokenAddresses.SKY[mainnet.id],
  [AssetIndex.LDO]: tokenAddresses.LDO[mainnet.id],
  [AssetIndex.AZTEC]: tokenAddresses.AZTEC[mainnet.id],
}

const USDC_PERPS_SYMBOL = 'USDC (Perps)'
const USDC_SPOT_SYMBOL = 'USDC (Spot)'
const ETH_SPOT_SYMBOL = 'ETH (Spot)'
const LINK_SPOT_SYMBOL = 'LINK (Spot)'
const UNI_SPOT_SYMBOL = 'UNI (Spot)'
const AAVE_SPOT_SYMBOL = 'AAVE (Spot)'
const SKY_SPOT_SYMBOL = 'SKY (Spot)'
const LDO_SPOT_SYMBOL = 'LDO (Spot)'
const LIT_SPOT_SYMBOL = 'LIT (Spot)'
const AZTEC_SPOT_SYMBOL = 'AZTEC (Spot)'

const generateLighterMainnetDepositActions = ({
  address,
  assetIndex,
  routeType,
}: {
  address: Address
  assetIndex: AssetIndex
  routeType: RouteType
}) => {
  // Create Lighter deposit calldata
  // deposit(address _to, uint16 _assetIndex, TxTypes.RouteType _routeType, uint256 _amount)
  const depositSelector = toFunctionSelector(
    'deposit(address,uint16,uint8,uint256)',
  ) as Hex

  const encodedLighterParams = encodeAbiParameters(
    [
      { type: 'address' },
      { type: 'uint16' },
      { type: 'uint8' },
      { type: 'uint256' },
    ],
    [address, assetIndex, routeType, AMOUNT_PLACEHOLDER],
  )

  // Lighter deposit calldata
  const lighterDepositCalldata = concat([depositSelector, encodedLighterParams])

  const depositToken = DEPOSIT_TOKEN_FOR_ASSET_INDEX[assetIndex]
  const tickSize = TICK_SIZE_FOR_ASSET_INDEX[assetIndex]
  const dustRecipient = address

  // Special handling of ETH Spot deposit flow as we need to unwrap WETH to ETH
  if (depositToken === NATIVE_TOKEN) {
    // VaultWrapper.deposit calldata for native ETH
    const vaultWrapperCalldata = encodeFunctionData({
      abi: VAULT_DEPOSITOR_ABI,
      functionName: 'depositWithTick',
      args: [
        depositToken, // token (native ETH)
        LIGHTER_DEPOSIT_ADDRESS, // vault (Lighter deposit contract)
        lighterDepositCalldata, // callData
        0n, // minAmountOut
        tickSize, // tickSize
        dustRecipient, // dustRecipient
      ],
    })
    // Flow: UnwrapWETHAndCall → VaultWrapper → Lighter deposit
    return [
      // Step 1: Approve WETH to UnwrapWETHAndCall
      getApprovalCalldata({
        tokenAddress: tokenAddresses.WETH[mainnet.id],
        spenderAddress: UNWRAP_AND_CALL_ADDRESS[mainnet.id],
        amount: MAX_APPROVAL,
      }),
      // Step 2: UnwrapWETHAndCall pulls WETH, unwraps to ETH, sends to VaultWrapper, calls deposit
      {
        contractAbi: UNWRAP_AND_CALL_ABI,
        contractAddress: UNWRAP_AND_CALL_ADDRESS[mainnet.id],
        functionName: 'unwrapAndCall',
        functionArgs: [
          VAULT_DEPOSITOR_ADDRESS[mainnet.id], // target (VaultWrapper)
          vaultWrapperCalldata, // VaultWrapper.deposit() calldata
        ],
      },
    ] satisfies FunkitCheckoutActionParams[]
  }

  const resetApprovalNeeded = shouldResetApproval(depositToken)

  // ERC20 tokens
  return [
    // Special handling of LDO token as it requires approval reset before approval can be set to a new value due to non-standard implementation of the approve function.
    ...(resetApprovalNeeded
      ? [
          getResetApprovalCalldata({
            tokenAddress: depositToken,
            spenderAddress: VAULT_DEPOSITOR_ADDRESS[mainnet.id],
          }),
        ]
      : []),

    // Step 1: Approve deposit token to VaultDepositor
    getApprovalCalldata({
      tokenAddress: depositToken,
      spenderAddress: VAULT_DEPOSITOR_ADDRESS[mainnet.id],
      amount: MAX_APPROVAL,
    }),
    // Step 2: VaultDepositor pulls deposit token and calls Lighter deposit
    {
      contractAbi: VAULT_DEPOSITOR_ABI,
      contractAddress: VAULT_DEPOSITOR_ADDRESS[mainnet.id],
      functionName: 'depositWithTick',
      functionArgs: [
        depositToken, // token (deposit token)
        LIGHTER_DEPOSIT_ADDRESS, // vault (Lighter deposit contract)
        lighterDepositCalldata, // callData
        0n, // minAmountOut
        tickSize, // tickSize
        dustRecipient, // dustRecipient
      ],
    },
  ] as FunkitCheckoutActionParams[]
}

const LIGHTER_USDC_PERPS_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.USDC[mainnet.id],
  targetAssetTicker: USDC_PERPS_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.USDC,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }

      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.USDC,
        routeType: RouteType.Perps,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_USDC_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.USDC[mainnet.id],
  targetAssetTicker: USDC_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.USDC,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }

      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.USDC,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_ETH_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.WETH[mainnet.id],
  targetAssetTicker: ETH_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.ETH,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }

      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.ETH,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

// LINK, UNI, AAVE, SKY, LDO tokens
const LIGHTER_LIT_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.LIT[mainnet.id],
  targetAssetTicker: LIT_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.LIT,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }
      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.LIT,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_LINK_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.LINK[mainnet.id],
  targetAssetTicker: LINK_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.LINK,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }

      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.LINK,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_SKY_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.SKY[mainnet.id],
  targetAssetTicker: SKY_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.SKY,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }

      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.SKY,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_UNI_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.UNI[mainnet.id],
  targetAssetTicker: UNI_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.UNI,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }
      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.UNI,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_AAVE_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.AAVE[mainnet.id],
  targetAssetTicker: AAVE_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.AAVE,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }
      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.AAVE,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_LDO_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.LDO[mainnet.id],
  targetAssetTicker: LDO_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.LDO,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }
      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.LDO,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const LIGHTER_AZTEC_SPOT_FUN_MAINNET_DEPOSIT = createPath({
  targetChain: mainnet.id.toString(),
  targetAsset: tokenAddresses.AZTEC[mainnet.id],
  targetAssetTicker: AZTEC_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.AZTEC,
  generateActionsParams:
    (address) => async (_finalTargetAssetAmount, outputConfig) => {
      if (!outputConfig) {
        return []
      }
      if (outputConfig.targetChain !== mainnet.id.toString()) {
        return []
      }
      return generateLighterMainnetDepositActions({
        address,
        assetIndex: AssetIndex.AZTEC,
        routeType: RouteType.Spot,
      }) as FunkitCheckoutActionParams[]
    },
})

const USDC_PERPS_RL_TRANSFER = createPath({
  targetChain: RELAY_LIGHTER_CHAIN_ID.toString(),
  targetAsset: RELAY_LIGHTER_USDC_PERPS_ADDRESS,
  targetAssetTicker: USDC_PERPS_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.USDC,
  getCustomRecipient: ({ l2Address }) => {
    return l2Address as FunAddress
  },
})

const USDC_SPOT_RL_TRANSFER = createPath({
  targetChain: RELAY_LIGHTER_CHAIN_ID.toString(),
  targetAsset: RELAY_LIGHTER_USDC_SPOT_ADDRESS,
  targetAssetTicker: USDC_SPOT_SYMBOL,
  iconSrc: ASSET_LOGO_SRCS.USDC,
  getCustomRecipient: ({ l2Address }) => {
    return l2Address as FunAddress
  },
})

export const LIGHTER_DYNAMIC_ROUTES: DynamicRoute[] = [
  createRoute({
    id: 'LIGHTER_ETH_SPOT',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_ETH_SPOT_FUN_MAINNET_DEPOSIT,
      RL_TRANSFER: createPath({
        targetChain: RELAY_LIGHTER_CHAIN_ID.toString(),
        targetAsset: RELAY_LIGHTER_ETH_SPOT_ADDRESS,
        targetAssetTicker: ETH_SPOT_SYMBOL,
        iconSrc: ASSET_LOGO_SRCS.ETH,
        getCustomRecipient: ({ l2Address }) => {
          return l2Address as FunAddress
        },
      }),
    },
  }),
  createRoute({
    id: 'LIGHTER_USDC_PERPS',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_USDC_PERPS_FUN_MAINNET_DEPOSIT,
      RL_TRANSFER: USDC_PERPS_RL_TRANSFER,
      ACROSS_TRANSFER: USDC_PERPS_RL_TRANSFER,
    },
  }),
  createRoute({
    id: 'LIGHTER_USDC_PERPS_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_USDC_PERPS_FUN_MAINNET_DEPOSIT,
      ACROSS_TRANSFER: USDC_PERPS_RL_TRANSFER,
    },
  }),
  createRoute({
    id: 'LIGHTER_USDC_SPOT',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_USDC_SPOT_FUN_MAINNET_DEPOSIT,
      RL_TRANSFER: USDC_SPOT_RL_TRANSFER,
      ACROSS_TRANSFER: USDC_SPOT_RL_TRANSFER,
    },
  }),
  createRoute({
    id: 'LIGHTER_USDC_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_USDC_SPOT_FUN_MAINNET_DEPOSIT,
      ACROSS_TRANSFER: USDC_SPOT_RL_TRANSFER,
    },
  }),
  createRoute({
    id: 'LIGHTER_ETH_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_ETH_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  // LINK, UNI, AAVE, SKY, LDO tokens
  createRoute({
    id: 'LIGHTER_LIT_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_LIT_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  createRoute({
    id: 'LIGHTER_LINK_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_LINK_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  createRoute({
    id: 'LIGHTER_SKY_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_SKY_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  createRoute({
    id: 'LIGHTER_UNI_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_UNI_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  createRoute({
    id: 'LIGHTER_AAVE_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_AAVE_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  createRoute({
    id: 'LIGHTER_LDO_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_LDO_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
  createRoute({
    id: 'LIGHTER_AZTEC_SPOT_NEW_USER',
    paths: {
      FUN_MAINNET_DEPOSIT: LIGHTER_AZTEC_SPOT_FUN_MAINNET_DEPOSIT,
    },
  }),
]
